import { createNqError } from './../../helper/createNqError';
import { createSlice } from "@reduxjs/toolkit";
import { ModemFinalDE, ModemNotRunReason, NQRecord, ModemFinalKind } from "@visonum/network-quality-sdk";
import { format } from "date-fns";
import FileSaver from "file-saver";
import { AppThunk } from "../../store";
import { reportNQError } from "../errors/errorSlice";
import { logger } from '../../helper/logger';
import { ConnectionModelIpVersionEnum, ModemFinalDidNotRunModelReasonEnum, ReportModelModemFinal } from '../../../generated/openapi/models';
import { getApiEndpointUrl, isScaVariant, nullOrValue } from '../../helper/utils';
import { API_KEY, baseUrl } from '../../config';
import { fetchRequest } from '../../helper/serverRequests';
import { PrintRequestModel } from '../../../generated/openapi/models/PrintRequestModel';
import getHints from '../../helper/getHints';
import { LangName } from '../../types';
import { limitSpeed } from '../../helper/utils/limitSpeed';

export enum PrintStage {
    None = "None",
    Request = "Request",
    SucceededResponse = "SucceededResponse",
}

export type PrintState = {
    kind: PrintStage.None
} | {
    kind: PrintStage.Request,
} | {
    kind: PrintStage.SucceededResponse,
}

const initialState = {
    kind: PrintStage.None
} as PrintState;

const createModemFinal = (modem: ModemFinalDE, provisionedDownloadSpeed: number | null): ReportModelModemFinal => {
    const modemFinal = modem.modemFinal;
    switch (modemFinal.kind) {
        case ModemFinalKind.ModemFinalAvailable:
            return {
                kind: ModemFinalKind.ModemFinalAvailable,
                value: limitSpeed(modemFinal.speed, provisionedDownloadSpeed)
            };
        case ModemFinalKind.ModemFinalDidNotRun:
            const createModemFinalDidNotRun = (): ReportModelModemFinal => {
                switch (modemFinal.reason) {
                    case ModemNotRunReason.NotNeeded:
                        return {
                            kind: ModemFinalKind.ModemFinalDidNotRun,
                            reason: ModemFinalDidNotRunModelReasonEnum.NotNeeded,
                        };
                    case ModemNotRunReason.NotAvailable:
                        return {
                            kind: ModemFinalKind.ModemFinalDidNotRun,
                            reason: ModemFinalDidNotRunModelReasonEnum.NotAvailable,
                        };
                    case ModemNotRunReason.NotACustomer:
                        return {
                            kind: ModemFinalKind.ModemFinalDidNotRun,
                            reason: ModemFinalDidNotRunModelReasonEnum.NotACustomer,
                        };
                }
            }
            return createModemFinalDidNotRun();
        case ModemFinalKind.ModemFinalFailed:
            return {
                kind: ModemFinalKind.ModemFinalFailed,
            };
    }
}

const createReportModel = (record: NQRecord, lang: LangName): PrintRequestModel => {
    const modemFinal = createModemFinal(record.modemFinal, nullOrValue(record.prepareResult.init.modem.provisionedDownloadSpeed));

    const isIPv6 = (ip: string) => {
        return ip.includes(":");
    }

    const tips = record.hints === null ?
        null :
        getHints(lang, isScaVariant())
            .filter(h => record.hints?.includes(h.id))
            .map(h => {
                return {
                    id: h.id,
                    title: h.title,
                    teaserTitle: h.teaserTitle,
                    teaser: h.teaser,
                    contentTitle: h.contentTitle,
                    content: h.content,
                };
            });

    return {
        lang,
        report: {
            speedtest: {
                ...record.prepareResult.init.speedtest,
                timestamp: record.date.toISOString(),
                tips
            },
            communication: {
                plannedImprovement: record.prepareResult.init.communication.plannedImprovement ?? undefined,
            },
            connection: {
                ...record.prepareResult.init.connection,
                ipVersion: isIPv6(record.prepareResult.init.connection.ip) ? ConnectionModelIpVersionEnum.V6 : ConnectionModelIpVersionEnum.V4,
            },
            modem: record.prepareResult.init.modem,
            client: {
                device: record.prepareResult.init.client.device,
            },
            cmts: record.prepareResult.init.cmts,
            result: {
                downloadFinal: {
                    value: record.downloadFinal.speed,
                    jitter: record.downloadFinal.jitter
                },
                uploadFinal: {
                    value: record.uploadFinal.speed,
                    jitter: record.uploadFinal.jitter
                },
                pingFinal: {
                    value: record.pingFinal.pingTime,
                    jitter: record.pingFinal.jitter
                },
                modemFinal
            }
        }
    };
}

const printRequest = async (record: NQRecord, lang: LangName): Promise<Blob> => {
    const printRequestModel = createReportModel(record, lang);

    try {
        const url = getApiEndpointUrl("/print/", baseUrl);
        const response = await fetchRequest(url, "POST", API_KEY, printRequestModel);
        const blob = await response.blob();
        return blob;
    } catch (err) {
        logger.error(err, "printRequest");
        throw err;
    }
};

export const printReport = (record: NQRecord, lang: LangName): AppThunk => (dispatch, getState) => {
    if (getState().print.kind !== PrintStage.None) {
        return;
    }
    dispatch(request());
    printRequest(record, lang).then(blob => {
        dispatch(succeededResponse());
        const fileName = `speedtestplus-${format(record.date, "yyyy-MM-dd")}-${record.prepareResult.init.speedtest.id}.pdf`;
        logger.info(`Saving file: ${fileName}`);
        FileSaver.saveAs(blob, fileName);
        dispatch(reset());
    }).catch((err: unknown) => {
        dispatch(reportNQError(createNqError(err) as Error));
        dispatch(reset());
    });
}

export const printSlice = createSlice({
    name: 'print',
    initialState,
    reducers: {
        reset: (state) => {
            return {
                kind: PrintStage.None
            }
        },
        request: (state) => {
            return {
                kind: PrintStage.Request
            }
        },
        succeededResponse: (state) => {
            return {
                kind: PrintStage.SucceededResponse
            }
        },
    },
});

export const { reset, request, succeededResponse } = printSlice.actions;

export default printSlice.reducer;